<template>
  <div class="app has-background-dark">
    <section class="section">
      <div class="container">
        <h1 class="title has-text-light">BB Pro Horse Racing Results - Dashboard</h1>
        
        <!-- Filters -->
        <div class="box has-background-grey-darker">
          <div class="columns is-multiline">
            <!-- Date Range -->
            <div class="column is-half">
              <label class="label has-text-light">Date Range</label>
              <div class="field has-addons">
                <div class="control">
                  <input type="date" class="input" v-model="tempFilters.startDate">
                </div>
                <div class="control">
                  <input type="date" class="input" v-model="tempFilters.endDate">
                </div>
              </div>
            </div>
            
            <!-- Odds Range -->
            <div class="column is-half">
              <label class="label has-text-light">Odds Range</label>
              <div class="field has-addons">
                <div class="control">
                  <input type="number" class="input" placeholder="Min Odds" v-model="tempFilters.oddsMin">
                </div>
                <div class="control">
                  <input type="number" class="input" placeholder="Max Odds" v-model="tempFilters.oddsMax">
                </div>
              </div>
            </div>
            
            <!-- EV Range -->
            <div class="column is-half">
              <label class="label has-text-light">EV Range

                <span class="icon is-small has-text-info ml-1 info-tooltip" data-tooltip="An EV rating of 100 is generally considered to be the minimum value worth betting on">
                  <i class="fas fa-info-circle"></i>
                </span>
              </label>
              <div class="field has-addons">
                <div class="control">
                  <input type="number" class="input" placeholder="Min EV" v-model="tempFilters.evMin">
                </div>
                <div class="control">
                  <input type="number" class="input" placeholder="Max EV" v-model="tempFilters.evMax">
                </div>
              </div>
            </div>
            
            <!-- Staking Methodology -->
            <div class="column is-half">
              <label class="label has-text-light">
                Staking Methodology
                <span class="icon is-small has-text-info ml-1 info-tooltip" data-tooltip="All bookmaker bets are considered each way and so any base stakes will be doubled (e.g. £5 Unit loss will be a total stake of £10)

                Unit Win (Target Profit) is based on the win and place combined.

                If you select Betfair SP the stake will be win only and so will not be doubled, and Unit Win (Target Profit) will be based on the win only.">
                  <i class="fas fa-info-circle"></i>
                </span>
              </label>
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model="tempFilters.stakingMethod">
                      <option value="">Select method</option>
                      <option value="kelly">Kelly</option>
                      <option value="ewkelly">BB Each Way</option>
                      <!-- <option value="unitWin">Unit Win (Target Profit)</option> Unit win based on the win part only. Remove for now on Duncans request  -->
                      <option value="eachwayUnitWin">Unit Win (Target Profit)</option><!-- Target profit based on the win and place combined -->
                      <option value="unitLoss">Unit Loss (Flat Stakes)</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <!-- Dynamic Staking Fields -->
              <div v-if="tempFilters.stakingMethod === 'kelly' || tempFilters.stakingMethod === 'ewkelly'" class="field mt-2">
                <div class="is-flex is-flex-wrap-wrap kelly-container">
                  <div class="kelly-inputs-container is-flex">
                    <div class="control">
                      <input type="number" class="input kelly-input" placeholder="Bankroll" v-model="tempFilters.bankroll">
                    </div>
                    <div class="control has-icons-right">
                      <input type="number" class="input kelly-input" placeholder="Kelly Fraction" v-model="tempFilters.kellyFraction">
                      <span class="icon is-small is-right has-text-grey-light">
                        <span>%</span>
                      </span>
                    </div>
                    <span class="kelly-tip icon is-small has-text-info info-tooltip" :data-tooltip="tempFilters.stakingMethod === 'ewkelly' ? 'This is a proprietary Bookie Bashing formula that modifies the optimal Kelly staking bet sizes specifically for each way bets. The percentage entered here (e.g. 10%) will multiply the BB Each Way stake by that amount as a fraction (e.g. 0.1) to reduce variance while maintaining positive expectation.' : 'Kelly staking calculates optimal bet sizes based on odds and edge. The percentage entered here (e.g. 10%) will multiply the Kelly stake by that amount as a fraction (e.g. 0.1) to reduce variance while maintaining positive expectation.'">
                      <i class="fas fa-info-circle"></i>
                    </span>
                  </div>
                  <div class="kelly-helpers-container is-flex">
                    <a v-if="tempFilters.stakingMethod !== 'ewkelly'" href="https://www.bookiebashing.net/hub/calculators/staking-with-kelly/" target="_blank" class="kelly-link">
                      Kelly Calculator
                      <span class="icon is-small">
                        <i class="fas fa-external-link-alt"></i>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
              <div v-if="tempFilters.stakingMethod === 'unitWin'" class="field mt-2">
                <div class="control">
                  <input type="number" class="input" placeholder="Amount to win per bet" v-model="tempFilters.unitWinAmount">
                </div>
              </div>
              <div v-if="tempFilters.stakingMethod === 'eachwayUnitWin'" class="field mt-2">
                <div class="control">
                  <input type="number" class="input" placeholder="Amount to win per bet" v-model="tempFilters.unitWinAmount">
                </div>
              </div>
              <div v-if="tempFilters.stakingMethod === 'unitLoss'" class="field mt-2">
                <div class="control">
                  <input type="number" class="input" placeholder="Amount to bet per bet" v-model="tempFilters.unitLossAmount">
                </div>
              </div>
            </div>
            
            <!-- Racecourse -->
            <div class="column is-half">
              <label class="label has-text-light">Racecourse</label>
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth is-multiple is-hidden-mobile multiselect-container racecourse-dropdown-container">
                    <div 
                      class="multiselect-header" 
                      @click="racecoursesDropdownOpen = !racecoursesDropdownOpen"
                    >
                      <span v-if="tempFilters.selectedRacecourses.length === 0">All Racecourses</span>
                      <span v-else-if="tempFilters.selectedRacecourses.length === 1">{{ tempFilters.selectedRacecourses[0] }}</span>
                      <span v-else>{{ tempFilters.selectedRacecourses.length }} racecourses selected</span>
                      <div class="multiselect-actions">
                        <button 
                          v-if="tempFilters.selectedRacecourses.length > 0"
                          class="button is-small is-ghost clear-button"
                          @click.stop="tempFilters.selectedRacecourses = []; filters.selectedRacecourses = []; applyFilters()"
                        >
                          <span class="icon is-small">
                            <i class="fas fa-times"></i>
                          </span>
                        </button>
                        <span class="icon is-small">
                          <i class="fas" :class="racecoursesDropdownOpen ? 'fa-angle-up' : 'fa-angle-down'"></i>
                        </span>
                      </div>
                    </div>
                    <div v-show="racecoursesDropdownOpen" class="multiselect-options">
                      <div 
                        v-for="course in racecourses" 
                        :key="course" 
                        class="multiselect-option"
                        :class="{ 'is-selected': tempFilters.selectedRacecourses.includes(course) }"
                        @click.stop="toggleSelection(tempFilters.selectedRacecourses, course)"
                      >
                        {{ course }}
                      </div>
                    </div>
                  </div>
                  <!-- Mobile friendly version -->
                  <div class="is-hidden-tablet">
                    <div class="racecourse-checkboxes">
                      <label v-for="course in racecourses" :key="course" class="checkbox racecourse-checkbox">
                        <input type="checkbox" :value="course" v-model="tempFilters.selectedRacecourses">
                        {{ course }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Data Metric -->
            <div class="column is-half">
              <label class="label has-text-light">Data Metric</label>
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model="tempFilters.dataMetric">
                      <option value="">All Metrics</option>
                      <option value="Default">Default</option>
                      <option value="BBAlgo">BB Algo</option>
                      <option value="both">Appears in both</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bookmaker -->
            <div class="column is-half">
              <label class="label has-text-light">Bookmaker</label>
              <div class="field">
                <div class="control">
                  <div class="select is-fullwidth is-multiple is-hidden-mobile multiselect-container bookmaker-dropdown-container">
                    <div 
                      class="multiselect-header" 
                      @click="bookmakersDropdownOpen = !bookmakersDropdownOpen"
                    >
                      <span v-if="tempFilters.selectedBookmakers.length === 0">All Bookmakers</span>
                      <span v-else-if="tempFilters.selectedBookmakers.length === 1">{{ tempFilters.selectedBookmakers[0] }}</span>
                      <span v-else>{{ tempFilters.selectedBookmakers.length }} bookmakers selected</span>
                      <div class="multiselect-actions">
                        <button 
                          v-if="tempFilters.selectedBookmakers.length > 0"
                          class="button is-small is-ghost clear-button"
                          @click.stop="tempFilters.selectedBookmakers = []; filters.selectedBookmakers = []; applyFilters()"
                        >
                          <span class="icon is-small">
                            <i class="fas fa-times"></i>
                          </span>
                        </button>
                        <span class="icon is-small">
                          <i class="fas" :class="bookmakersDropdownOpen ? 'fa-angle-up' : 'fa-angle-down'"></i>
                        </span>
                      </div>
                    </div>
                    <div v-show="bookmakersDropdownOpen" class="multiselect-options">
                      <div 
                        v-for="bookmaker in bookmakers" 
                        :key="bookmaker" 
                        class="multiselect-option"
                        :class="{ 'is-selected': tempFilters.selectedBookmakers.includes(bookmaker) }"
                        @click.stop="toggleSelection(tempFilters.selectedBookmakers, bookmaker)"
                      >
                        {{ bookmaker }}
                      </div>
                    </div>
                  </div>
                  <!-- Mobile friendly version -->
                  <div class="is-hidden-tablet">
                    <div class="bookmaker-checkboxes">
                      <label v-for="bookmaker in bookmakers" :key="bookmaker" class="checkbox bookmaker-checkbox">
                        <input type="checkbox" :value="bookmaker" v-model="tempFilters.selectedBookmakers">
                        {{ bookmaker }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Month of Year and Day of Week on the same row -->
            <div class="column is-half">
              <div class="columns is-gapless">
                <!-- Month of Year -->
                <div class="column is-half pr-1">
                  <label class="label has-text-light">Month</label>
                  <div class="field">
                    <div class="control">
                      <div class="select is-fullwidth is-multiple is-hidden-mobile multiselect-container month-dropdown-container">
                        <div 
                          class="multiselect-header" 
                          @click="monthsDropdownOpen = !monthsDropdownOpen"
                        >
                          <span v-if="tempFilters.selectedMonths.length === 0">All Months</span>
                          <span v-else-if="tempFilters.selectedMonths.length === 1">{{ monthOptions.find(m => m.value === tempFilters.selectedMonths[0]).label }}</span>
                          <span v-else>{{ tempFilters.selectedMonths.length }} months selected</span>
                          <div class="multiselect-actions">
                            <button 
                              v-if="tempFilters.selectedMonths.length > 0"
                              class="button is-small is-ghost clear-button"
                              @click.stop="tempFilters.selectedMonths = []; filters.selectedMonths = []; applyFilters()"
                            >
                              <span class="icon is-small">
                                <i class="fas fa-times"></i>
                              </span>
                            </button>
                            <span class="icon is-small">
                              <i class="fas" :class="monthsDropdownOpen ? 'fa-angle-up' : 'fa-angle-down'"></i>
                            </span>
                          </div>
                        </div>
                        <div v-show="monthsDropdownOpen" class="multiselect-options">
                          <div 
                            v-for="month in monthOptions" 
                            :key="month.value" 
                            class="multiselect-option"
                            :class="{ 'is-selected': tempFilters.selectedMonths.includes(month.value) }"
                            @click.stop="toggleSelection(tempFilters.selectedMonths, month.value)"
                          >
                            {{ month.label }}
                          </div>
                        </div>
                      </div>
                      <!-- Mobile friendly version -->
                      <div class="is-hidden-tablet">
                        <div class="month-checkboxes">
                          <label v-for="month in monthOptions" :key="month.value" class="checkbox month-checkbox">
                            <input type="checkbox" :value="month.value" v-model="tempFilters.selectedMonths">
                            {{ month.label }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Day of Week -->
                <div class="column is-half pl-1">
                  <label class="label has-text-light">Day of Week</label>
                  <div class="field">
                    <div class="control">
                      <div class="select is-fullwidth is-multiple is-hidden-mobile multiselect-container day-dropdown-container">
                        <div 
                          class="multiselect-header" 
                          @click="daysDropdownOpen = !daysDropdownOpen"
                        >
                          <span v-if="tempFilters.selectedDays.length === 0">All Days</span>
                          <span v-else-if="tempFilters.selectedDays.length === 1">{{ dayOptions.find(d => d.value === tempFilters.selectedDays[0]).label }}</span>
                          <span v-else>{{ tempFilters.selectedDays.length }} days selected</span>
                          <div class="multiselect-actions">
                            <button 
                              v-if="tempFilters.selectedDays.length > 0"
                              class="button is-small is-ghost clear-button"
                              @click.stop="tempFilters.selectedDays = []; filters.selectedDays = []; applyFilters()"
                            >
                              <span class="icon is-small">
                                <i class="fas fa-times"></i>
                              </span>
                            </button>
                            <span class="icon is-small">
                              <i class="fas" :class="daysDropdownOpen ? 'fa-angle-up' : 'fa-angle-down'"></i>
                            </span>
                          </div>
                        </div>
                        <div v-show="daysDropdownOpen" class="multiselect-options">
                          <div 
                            v-for="day in dayOptions" 
                            :key="day.value" 
                            class="multiselect-option"
                            :class="{ 'is-selected': tempFilters.selectedDays.includes(day.value) }"
                            @click.stop="toggleSelection(tempFilters.selectedDays, day.value)"
                          >
                            {{ day.label }}
                          </div>
                        </div>
                      </div>
                      <!-- Mobile friendly version -->
                      <div class="is-hidden-tablet">
                        <div class="day-checkboxes">
                          <label v-for="day in dayOptions" :key="day.value" class="checkbox day-checkbox">
                            <input type="checkbox" :value="day.value" v-model="tempFilters.selectedDays">
                            {{ day.label }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="box has-background-grey-darker mb-4">
            <div class="columns is-multiline is-mobile">
              <div class="column is-4-desktop is-12-tablet is-12-mobile">
                <div class="is-flex is-align-items-center is-justify-content-center">
                  <div class="buttons has-addons mb-0">
                    <button 
                      class="button" 
                      :class="{ 'is-info': !showDuplicates }"
                      @click="showDuplicates = false; applyFilters()"
                    >
                      No Duplicates
                    </button>
                    <button 
                      class="button" 
                      :class="{ 'is-info': showDuplicates }"
                      @click="showDuplicates = true; applyFilters()"
                    >
                      All Results
                    </button>
                  </div>
                  <span class="icon is-small has-text-info ml-3 info-tooltip button-info-icons" data-tooltip="All Results will show every single bet we tracked, including duplicates across the different bookmakers
                  
                  No Duplicates will take the first time a horse was registered on any given date">
                    <i class="fas fa-info-circle"></i>
                  </span>
                </div>
              </div>
              <div class="column is-4-desktop is-12-tablet is-12-mobile">
                <div class="is-flex is-align-items-center is-justify-content-center">
                  <div class="buttons has-addons mb-0">
                    <button 
                      class="button" 
                      :class="{ 'is-info': tempFilters.oddsType === 'book_odds' }"
                      @click="tempFilters.oddsType = 'book_odds'; filters.oddsType = 'book_odds'; applyFilters()"
                    >
                      Book Odds
                    </button>
                    <button 
                      class="button" 
                      :class="{ 'is-info': tempFilters.oddsType === 'bfsp' }"
                      @click="tempFilters.oddsType = 'bfsp'; filters.oddsType = 'bfsp'; applyFilters()"
                    >
                      Betfair SP
                    </button>
                  </div>
                  <span class="icon is-small has-text-info ml-3 info-tooltip button-info-icons" data-tooltip="Book Odds are each way bets and prices are taken at the time of sampling the data in the racing tracker tool
                  
                  Betfair SP are the same horses, but win only bets and settled at Betfair SP which is the odds given by Betfair at the start of each race">
                    <i class="fas fa-info-circle"></i>
                  </span>
                </div>
              </div>
              <div class="column is-4-desktop is-12-tablet is-12-mobile">
                <div class="is-flex is-align-items-center is-justify-content-center">
                  <div class="buttons has-addons mb-0">
                    <button 
                      class="button" 
                      :class="{ 'is-info': hideWinArbs }"
                      @click="hideWinArbs = true; applyFilters()"
                    >
                      Hide Win Arbs
                    </button>
                    <button 
                      class="button" 
                      :class="{ 'is-info': !hideWinArbs }"
                      @click="hideWinArbs = false; applyFilters()"
                    >
                      Show All
                    </button>
                  </div>
                  <span class="icon is-small has-text-info ml-3 info-tooltip button-info-icons" data-tooltip="Hide Win Arbs will filter out bets where the bookmaker odds are greater than or equal to the win fair odds
                  
                  Show All will display all bets regardless of the odds comparison">
                    <i class="fas fa-info-circle"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="field is-grouped is-grouped-centered mt-4">
            <div class="control">
              <button class="button is-success is-large" @click="getResults" :disabled="isLoading">
                <span v-if="isLoading" class="icon">
                  <i class="fas fa-spinner fa-spin"></i>
                </span>
                <span>Filter Results</span>
              </button>
            </div>
            <div class="control">
              <button class="button is-info is-large" @click="downloadResults" :disabled="!hasResults || isLoading">
                Download Results
              </button>
            </div>
          </div>
          
          <!-- Loading Message -->
          <div v-if="isLoading" class="notification is-info mt-4">
            <p class="has-text-centered">
              <span class="icon">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
              Loading results...
            </p>
          </div>
        </div>
        
        <!-- Results Section -->
        <div v-if="hasResults && !isLoading" class="mt-6">
          <!-- Stats Boxes -->
          <div class="columns is-multiline mb-4">
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker py-2">
                <p class="has-text-light has-text-centered mb-1">Total Bets: {{ kpis.totalBets }}</p>
              </div>
            </div>
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker py-2">
                <p class="has-text-light has-text-centered mb-1">Wins: {{ kpis.wins }}</p>
              </div>
            </div>
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker py-2">
                <p class="has-text-light has-text-centered mb-1">Places: {{ kpis.places }}</p>
              </div>
            </div>
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker py-2">
                <p class="has-text-light has-text-centered mb-1">Losses: {{ kpis.losses }}</p>
              </div>
            </div>
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker py-2">
                <p class="has-text-light has-text-centered mb-1">Strike Rate: {{ typeof kpis.strikeRate === 'number' ? kpis.strikeRate.toFixed(1) + '%' : kpis.strikeRate }}</p>
              </div>
            </div>
          </div>

          <!-- KPI Cards -->
          <div class="columns is-multiline">
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker">
                <h2 class="title is-4 has-text-light has-text-centered">ROI %</h2>
                <p class="subtitle is-3 has-text-centered" :class="{ 'has-text-primary': kpis.roi > 0, 'has-text-danger': kpis.roi <= 0 }">
                  {{ kpis.roi.toFixed(2) }}%
                </p>
              </div>
            </div>
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker">
                <h2 class="title is-4 has-text-light has-text-centered">Amount Staked</h2>
                <p class="subtitle is-3 has-text-primary has-text-centered">£{{ Number(kpis.amountStaked).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</p>
              </div>
            </div>
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker">
                <h2 class="title is-4 has-text-light has-text-centered">P/L</h2>
                <p class="subtitle is-3" :class="profitLossClass">£{{ Number(kpis.profitLoss).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</p>
              </div>
            </div>
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker">
                <h2 class="title is-4 has-text-light has-text-centered">Average Odds</h2>
                <p class="subtitle is-3 has-text-primary has-text-centered">
                  {{ kpis.averageOdds.toFixed(2) }}
                </p>
              </div>
            </div>
            <div class="column is-one-fifth">
              <div class="box has-background-grey-darker">
                <h2 class="title is-4 has-text-light has-text-centered">Max Drawdown</h2>
                <p class="subtitle is-3 has-text-danger has-text-centered">£{{ Number(kpis.maxDrawdown).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</p>
              </div>
            </div>
          </div>
          
          <!-- Results Chart -->
          <div class="box has-background-grey-darker p-6" style="height: 400px">
            <LineChart 
              v-if="chartData" 
              :data="chartData" 
              :options="chartOptions" 
            />
          </div>
        </div>
      </div>
      <!-- Results Table -->
      <BetsTable 
        v-if="hasResults && !isLoading" 
        :bets="filteredResults"
        :filters="filters"
        :bankroll="filters.bankroll"
        :kelly-fraction="filters.kellyFraction"
        class="mt-6"
      />
    </section>
  </div>
</template>

<script>
import { ref, computed, shallowRef, markRaw, onMounted, onUnmounted } from 'vue'
import { Line as LineChart } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js'
import BetsTable from './components/BetsTable.vue'
import { calculateBetDetails } from './utils/betting'
import { fetchWithCacheBusting } from './utils/cacheUtils'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

export default {
  name: 'App',
  components: { 
    LineChart,
    BetsTable 
  },
  
  setup() {
    const showDuplicates = ref(false)
    const hideWinArbs = ref(true)
    const results = shallowRef([])
    const isLoading = ref(true)
    const monthsDropdownOpen = ref(false)
    const daysDropdownOpen = ref(false)
    const racecoursesDropdownOpen = ref(false)
    const bookmakersDropdownOpen = ref(false)
    // tempFilters hold the input values until "Filter Results" is pressed.
    const tempFilters = ref({
      startDate: '',
      endDate: '',
      evMin: null,
      evMax: null,
      oddsMin: null,
      oddsMax: null,
      stakingMethod: '',
      bankroll: 10000,
      kellyFraction: 10,
      unitWinAmount: null,
      unitLossAmount: null,
      racecourse: '',
      selectedRacecourses: [],
      dataMetric: '',
      oddsType: 'book_odds',
      bookmaker: '',
      selectedBookmakers: [],
      selectedMonths: [],
      selectedDays: []
    })
    // filters hold the committed filter values used in processing.
    const filters = ref({ ...tempFilters.value })
    
    // Month and day options
    const monthOptions = [
      { value: 0, label: 'January' },
      { value: 1, label: 'February' },
      { value: 2, label: 'March' },
      { value: 3, label: 'April' },
      { value: 4, label: 'May' },
      { value: 5, label: 'June' },
      { value: 6, label: 'July' },
      { value: 7, label: 'August' },
      { value: 8, label: 'September' },
      { value: 9, label: 'October' },
      { value: 10, label: 'November' },
      { value: 11, label: 'December' }
    ]
    
    const dayOptions = [
      { value: 0, label: 'Sunday' },
      { value: 1, label: 'Monday' },
      { value: 2, label: 'Tuesday' },
      { value: 3, label: 'Wednesday' },
      { value: 4, label: 'Thursday' },
      { value: 5, label: 'Friday' },
      { value: 6, label: 'Saturday' }
    ]
    
    // Bookmaker options
    const bookmakers = [
      'Bet365',
      'Betfred',
      'Ladbrokes',
      'Skybet',
      'William Hill',
      'Paddy Power'
    ]
    
    // Manual filtering function; results are sorted by created date.
    const filteredResultsRef = ref([])
    const applyFilters = () => {
      if (isLoading.value || !results.value.length) {
        filteredResultsRef.value = []
        return
      }

      // First, handle "Appears in both" metric if selected
      let filteredBets = [...results.value]

      // Filter out invalid fair odds (less than 1)
      filteredBets = filteredBets.filter(bet => {
        const winFairOdds = parseFloat(bet.win_fair_odds)
        const placeFairOdds = parseFloat(bet.place_fair_odds)
        return winFairOdds >= 1 && placeFairOdds >= 1
      })

      if (filters.value.dataMetric === 'both') {
        // Create a map to group bets by horse/event/the_off
        const betGroups = new Map()
        filteredBets.forEach(bet => {
          const key = `${bet.sel_name}_${bet.event}_${bet.the_off}`
          if (!betGroups.has(key)) {
            betGroups.set(key, { bets: [], metrics: new Set() })
          }
          betGroups.get(key).bets.push(bet)
          betGroups.get(key).metrics.add(bet.metric)
        })
        
        // Keep only bets that appear in both metrics
        filteredBets = Array.from(betGroups.values())
          .filter(group => group.metrics.has('Default') && group.metrics.has('BBAlgo'))
          .flatMap(group => group.bets)
      } else {
        // Filter by specific metric if selected
        if (filters.value.dataMetric && filters.value.dataMetric !== 'All Metrics') {
          filteredBets = filteredBets.filter(bet => bet.metric === filters.value.dataMetric)
        }
      }

      // Apply other filters
      filteredBets = filteredBets.filter(bet => {
        const odds = parseFloat(bet[filters.value.oddsType])
        if (isNaN(odds)) return false
        
        // Filter out win arbs if hideWinArbs is true
        if (hideWinArbs.value) {
          const bookOdds = parseFloat(bet.book_odds)
          const winFairOdds = parseFloat(bet.win_fair_odds)
          if (!isNaN(bookOdds) && !isNaN(winFairOdds) && bookOdds >= winFairOdds) {
            return false
          }
        }
        
        // Date filter
        if (filters.value.startDate || filters.value.endDate) {
          const date = new Date(parseInt(bet.created))
          const startDate = filters.value.startDate ? new Date(filters.value.startDate) : null
          const endDate = filters.value.endDate ? new Date(filters.value.endDate) : null
          
          // Set start date to beginning of day
          if (startDate) {
            startDate.setHours(0, 0, 0, 0)
            if (date < startDate) return false
          }
          
          // Set end date to end of day
          if (endDate) {
            endDate.setHours(23, 59, 59, 999)
            if (date > endDate) return false
          }
        }
        // Odds filter
        if (filters.value.oddsMin && odds < parseFloat(filters.value.oddsMin)) return false
        if (filters.value.oddsMax && odds > parseFloat(filters.value.oddsMax)) return false
        // EV filter
        if (filters.value.evMin && parseFloat(bet.ev_taken) < parseFloat(filters.value.evMin)) return false
        if (filters.value.evMax && parseFloat(bet.ev_taken) > parseFloat(filters.value.evMax)) return false
        // Racecourse filter
        if (filters.value.selectedRacecourses && filters.value.selectedRacecourses.length > 0) {
          const eventLower = bet.event.toLowerCase();
          const found = filters.value.selectedRacecourses.some(course => 
            eventLower.includes(course.toLowerCase())
          );
          if (!found) return false;
        }
        
        // Bookmaker filter
        if (filters.value.selectedBookmakers && filters.value.selectedBookmakers.length > 0) {
          if (!filters.value.selectedBookmakers.includes(bet.bookie_name)) return false;
        }
        
        // Month of the year filter
        if (filters.value.selectedMonths && filters.value.selectedMonths.length > 0) {
          const betDate = new Date(parseInt(bet.created))
          const betMonth = betDate.getMonth() // 0-based month (0 = January, 11 = December)
          if (!filters.value.selectedMonths.includes(betMonth)) return false
        }
        
        // Day of the week filter
        if (filters.value.selectedDays && filters.value.selectedDays.length > 0) {
          const betDate = new Date(parseInt(bet.created))
          const betDay = betDate.getDay() // 0-based day (0 = Sunday, 6 = Saturday)
          if (!filters.value.selectedDays.includes(betDay)) return false
        }
        
        return true
      })

      // Always sort by date (ascending)
      filteredBets = [...filteredBets].sort((a, b) => parseInt(a.created) - parseInt(b.created))
      
      // Handle duplicate filtering after all other filters
      if (!showDuplicates.value) {
        const seen = new Map()
        filteredBets = filteredBets.filter(bet => {
          const key = `${bet.sel_name}_${bet.event}_${bet.the_off}`
          if (!seen.has(key)) {
            seen.set(key, true)
            return true
          }
          return false
        })
      }

      // Calculate bet details once for each filtered bet
      const bankroll = parseFloat(filters.value.bankroll) || 0
      const kellyFraction = parseFloat(filters.value.kellyFraction) || 100
      let runningPL = 0
      filteredBets = filteredBets.map(bet => {
        const betDetails = calculateBetDetails(bet, filters.value, bankroll, kellyFraction)
        runningPL += betDetails.profit
        return {
          ...bet,
          betDetails: {
            ...betDetails,
            runningPL
          }
        }
      })

      filteredResultsRef.value = filteredBets
    }
    
    const hasResults = computed(() => results.value.length > 0)
    
    const racecourses = computed(() => {
      const courses = new Set()
      results.value.forEach(result => {
        const parts = result.event.split(' ')
        if (parts.length > 1) courses.add(parts[1])
      })
      return Array.from(courses).sort()
    })
    
    const kpis = computed(() => {
      const filtered = filteredResultsRef.value
      let totalStake = 0, totalProfit = 0, runningPL = 0, maxDrawdown = 0, peakBalance = 0
      let currentBankroll = parseFloat(filters.value.bankroll) || 10000
      let totalOdds = 0
      
      const wins = filtered.filter(r => r.result === 'Win').length
      const places = filters.value.oddsType === 'bfsp' ? 'N/A' : filtered.filter(r => r.result === 'Place').length
      const losses = filters.value.oddsType === 'bfsp' 
        ? filtered.filter(r => r.result !== 'Win' && r.result !== 'Non Runner').length 
        : filtered.filter(r => r.result === 'Loss').length
      const totalBets = filtered.length
      
      filtered.forEach(result => {
        // Use pre-calculated bet details
        const { stake, profit } = result.betDetails
        totalStake += stake
        runningPL += profit
        totalProfit += profit
        if (filters.value.stakingMethod === 'kelly') {
          currentBankroll += profit
        }
        peakBalance = Math.max(peakBalance, runningPL)
        maxDrawdown = Math.min(maxDrawdown, runningPL - peakBalance)
        totalOdds += parseFloat(result[filters.value.oddsType])
      })
      
      return {
        roi: totalStake ? (totalProfit / totalStake) * 100 : 0,
        amountStaked: totalStake,
        profitLoss: totalProfit,
        averageOdds: totalBets ? totalOdds / totalBets : 0,
        maxDrawdown: Math.abs(maxDrawdown),
        totalBets,
        wins,
        places,
        losses,
        strikeRate: totalBets ? (wins / totalBets) * 100 : 0,
        placeRate: filters.value.oddsType === 'bfsp' ? 'N/A' : totalBets ? ((wins + places) / totalBets) * 100 : 0
      }
    })
    
    const chartData = computed(() => {
      const filtered = filteredResultsRef.value
      if (!filtered.length) return null
      return {
        labels: filtered.map(r => new Date(parseInt(r.created)).toLocaleDateString()),
        datasets: [{
          label: 'Running P/L',
          data: filtered.map(result => result.betDetails.runningPL),
          borderColor: '#00d1b2',
          tension: 0.1,
          fill: false,
          pointRadius: 0
        }]
      }
    })
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          grid: { color: '#4a4a4a' },
          ticks: { 
            color: '#b5b5b5', 
            callback: value => '£' + value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
          }
        },
        x: {
          grid: { color: '#4a4a4a' },
          ticks: { color: '#b5b5b5', maxRotation: 45, minRotation: 45 }
        }
      },
      plugins: {
        legend: { labels: { color: '#b5b5b5' } },
        tooltip: { 
          callbacks: { 
            label: context => '£' + context.parsed.y.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
          } 
        }
      }
    }
    
    const loadResults = async () => {
      try {
        isLoading.value = true
        console.log('Fetching results...')
        
        // Use the utility function for fetching with cache busting
        const startTime = performance.now()
        const data = await fetchWithCacheBusting(`/racing_results/horse_tracker_results.json`)

        const loadTime = performance.now() - startTime
        
        console.log(`Total load time: ${loadTime.toFixed(2)}ms`)
        
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received')
        }
        
        results.value = markRaw(data)
        console.log('Initial data:', {
          count: data.length,
          sampleBet: data[0],
          dateRange: {
            start: new Date(parseInt(data[0].created)).toISOString(),
            end: new Date(parseInt(data[data.length - 1].created)).toISOString()
          }
        })
        const uniqueBetsMap = new Map()
        data.forEach(bet => {
          const key = `${bet.sel_name}_${bet.event}_${bet.created}`
          if (!uniqueBetsMap.has(key)) {
            uniqueBetsMap.set(key, bet)
          }
        })
        results.value = markRaw(Array.from(uniqueBetsMap.values()))
      } catch (error) {
        console.error('Error loading results:', error)
      } finally {
        isLoading.value = false
      }
    }
    
    const getResults = async () => {
      if (!results.value.length) {
        console.log('No data loaded, loading now...')
        await loadResults()
      }
      // Commit tempFilters to filters and then apply filtering.
      filters.value = { ...tempFilters.value }
      // Apply filters will use the current value of hideWinArbs
      applyFilters()
      console.log('Filtered results:', filteredResultsRef.value)
    }
    
    const downloadResults = () => {
      const filtered = filteredResultsRef.value
      const formatRaceTime = (timestamp) => {
        const date = new Date(parseInt(timestamp))
        return date.toLocaleDateString() + ' ' + 
               date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
      }

      const csv = [
        ['Horse', 'Event', 'Race Time', '#Books', 'Bookie Name', 'Win Fair Odds', 'Place Fair Odds', 'Book Odds', 'BFSP', 'Terms', 'Places', 'EV', 'Result', 'Position', 'Stake', 'Profit', 'Cumulative Profit', 'Metric'].join(','),
        ...filtered.map(r => [
          r.sel_name,
          r.event,
          formatRaceTime(r.the_off * 1000),
          r.books,
          r.bookie_name,
          r.win_fair_odds,
          r.place_fair_odds,
          r.book_odds,
          r.bfsp,
          r.terms,
          r.places,
          r.ev_taken,
          r.result,
          r.position,
          (r.betDetails?.stake || 5.00).toFixed(2),
          r.betDetails.profit.toFixed(2),
          r.betDetails.runningPL.toFixed(2),
          r.metric
        ].join(','))
      ].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.setAttribute('href', url)
      const date = new Date().toISOString().split('T')[0]
      a.setAttribute('download', `racing_results_${date}.csv`)
      a.click()
    }
    
    // Function to toggle a value in an array
    const toggleSelection = (array, value) => {
      const index = array.indexOf(value)
      if (index === -1) {
        array.push(value)
      } else {
        array.splice(index, 1)
      }
    }
    
    // Close dropdowns when clicking outside
    const closeDropdowns = (event) => {
      const monthsDropdownContainer = document.querySelector('.month-dropdown-container')
      const daysDropdownContainer = document.querySelector('.day-dropdown-container')
      const racecoursesDropdownContainer = document.querySelector('.racecourse-dropdown-container')
      const bookmakersDropdownContainer = document.querySelector('.bookmaker-dropdown-container')
      
      if (!monthsDropdownContainer?.contains(event.target)) {
        monthsDropdownOpen.value = false
      }
      
      if (!daysDropdownContainer?.contains(event.target)) {
        daysDropdownOpen.value = false
      }
      
      if (!racecoursesDropdownContainer?.contains(event.target)) {
        racecoursesDropdownOpen.value = false
      }
      
      if (!bookmakersDropdownContainer?.contains(event.target)) {
        bookmakersDropdownOpen.value = false
      }
    }
    
    // Add click event listener when component is mounted
    onMounted(() => {
      document.addEventListener('click', closeDropdowns)
    })
    
    // Remove click event listener when component is unmounted
    onUnmounted(() => {
      document.removeEventListener('click', closeDropdowns)
    })
    
    // Initialize tempFilters from filters.
    tempFilters.value = { ...filters.value }
    loadResults()
    
    return {
      filters,
      tempFilters,
      hasResults,
      isLoading,
      kpis,
      chartData,
      chartOptions,
      racecourses,
      monthOptions,
      dayOptions,
      bookmakers,
      getResults,
      downloadResults,
      filteredResults: filteredResultsRef,
      profitLossClass: computed(() => ({
        'has-text-success has-text-centered': kpis.value.profitLoss > 0,
        'has-text-danger has-text-centered': kpis.value.profitLoss <= 0
      })),
      showDuplicates,
      hideWinArbs,
      applyFilters,
      monthsDropdownOpen,
      daysDropdownOpen,
      racecoursesDropdownOpen,
      bookmakersDropdownOpen,
      toggleSelection
    }
  }
}
</script>

<style>
.app {
  min-height: 100vh;
}
.box {
  border: 1px solid #363636;
}
.input, .select select {
  background-color: #363636;
  border-color: #4a4a4a;
  color: #b5b5b5;
}
.input:focus, .select select:focus {
  border-color: #00d1b2;
}
.select:not(.is-multiple):not(.is-loading)::after {
  border-color: #b5b5b5;
}
.label {
  color: #b5b5b5;
}
.button {
  border: none;
}
.button.is-primary {
  background-color: #00d1b2;
}
.button.is-info {
  background-color: #3298dc;
}
.input::-webkit-input-placeholder, .select select::-webkit-input-placeholder, .textarea::-webkit-input-placeholder {
  color: #b5b5b5;
}
.progress {
  margin-top: 1rem;
  height: 1.5rem;
}
.progress::-webkit-progress-value {
  transition: width 0.5s ease;
}
.buttons.has-addons .button {
  margin-right: 0;
}
.buttons.has-addons .button:not(:last-child) {
  border-right: 1px solid #4a4a4a;
}
.button:not(.is-info) {
  background-color: #363636;
  color: #b5b5b5;
}
.button:not(.is-info):hover {
  background-color: #404040;
  color: white;
}
.button.is-success {
    background-color: #48c78e;
    border-color: transparent;
    color: #fff;
}
.button.is-success:hover {
    background-color: #3ec487;
}

/* Toggle buttons responsive styling */
.buttons.has-addons {
  width: 100%;
  max-width: 240px;
  justify-content: center;
}

.buttons.has-addons .button {
  flex: 1;
  text-align: center;
  white-space: nowrap;
}

@media screen and (max-width: 1023px) {
  .column.is-12-tablet {
    margin-bottom: 1rem;
  }
  
  .column.is-12-tablet:last-child {
    margin-bottom: 0;
  }
}

@media screen and (max-width: 768px) {
  .column.is-12-mobile {
    margin-bottom: 1rem;
  }
  
  .column.is-12-mobile:last-child {
    margin-bottom: 0;
  }
}

/* Custom tooltip styles */
.info-tooltip {
  position: relative;
  cursor: help;
}

.info-tooltip:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  margin-top: 11px;
  padding: 8px;
  background: #cbe4f5;
  color: #121b20;
  border-radius: 4px;
  font-weight: 500;
  border: 1px solid #121b20;
  font-size: 0.875rem;
  width: max-content;
  max-width: 360px;
  white-space: pre-line;
  z-index: 1000;
}

.button-info-icons {
  top: -3px;
}

.kelly-input {
  width: 175px;
}

.kelly-container {
  align-items: center;
}

.kelly-inputs-container {
  gap: 0.5rem;
}

.kelly-helpers-container {
  align-items: center;
  gap: 0.75rem;
  margin-left: 0.5rem;
}

.kelly-tip {
  position: relative;
  top: 11px;
  left: 2px;
}

.kelly-link {
  display: flex;
  align-items: center;
  color: #3298dc;
  font-size: 0.9rem;
  text-decoration: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.kelly-link:hover {
  color: #2186c7;
  background-color: rgba(50, 152, 220, 0.1);
}

.kelly-link .icon {
  margin-left: 0.25rem;
  font-size: 0.8rem;
  top: 2px;
  position: relative;
}

@media screen and (max-width: 768px) {
  .kelly-helpers-container {
    width: 100%;
    margin-top: 0.75rem;
    margin-left: 0;
    justify-content: flex-start;
    padding-left: 0.5rem;
  }
}

/* Multi-select styling */
.multiselect-container {
  position: relative;
}

.multiselect-header {
  background-color: #363636;
  color: #b5b5b5;
  border: 1px solid #4a4a4a;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.multiselect-header:hover {
  background-color: #404040;
}

.multiselect-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: #363636;
  border: 1px solid #4a4a4a;
  border-radius: 4px;
  color: #b5b5b5;
  max-height: 240px;
  overflow-y: auto;
  z-index: 100;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
}

.multiselect-option {
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
}

.multiselect-option:hover {
  background-color: #404040;
}

.multiselect-option.is-selected {
  background-color: #3298dc;
  color: white;
}

.multiselect-dropdown {
  background-color: #363636;
  color: #b5b5b5;
  border-color: #4a4a4a;
  overflow-y: auto;
}

.multiselect-dropdown[size="6"] {
  position: absolute;
  z-index: 100;
  width: 100%;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  max-height: 240px;
}

.multiselect-dropdown:focus {
  border-color: #3298dc;
}

.multiselect-dropdown option {
  padding: 8px 12px;
}

.multiselect-dropdown option:checked {
  background: #3298dc linear-gradient(0deg, #3298dc 0%, #3298dc 100%);
  color: white;
}

/* Mobile checkbox lists */
.month-checkboxes, .day-checkboxes, .racecourse-checkboxes, .bookmaker-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: #363636;
  border: 1px solid #4a4a4a;
  border-radius: 4px;
  max-height: 150px;
  overflow-y: auto;
}

.month-checkbox, .day-checkbox, .racecourse-checkbox, .bookmaker-checkbox {
  display: flex;
  align-items: center;
  color: #b5b5b5;
  padding: 0.25rem 0.5rem;
  background-color: #404040;
  border-radius: 4px;
  margin: 0 !important;
  font-size: 0.9rem;
}

.month-checkbox input, .day-checkbox input, .racecourse-checkbox input, .bookmaker-checkbox input {
  margin-right: 0.5rem;
}

/* Keep multiselect dropdowns from being too cramped */
.column.is-half .columns.is-gapless .multiselect-options {
  max-height: 300px;
  overflow-y: auto;
}

.multiselect-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.clear-button {
  padding: 0;
  height: 20px;
  width: 20px;
  color: #b5b5b5;
  transition: all 0.2s ease;
}

.clear-button:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.clear-button .icon {
  height: 20px;
  width: 20px;
}

.clear-button .icon i {
  font-size: 0.8rem;
}
</style>